import React, { useState } from 'react';
import EventStatus from '../status/EventStatus';
import StatusDot from '../status/StatusDot';
import ParticipantGroupDot from '../participant-group/ParticipantGroupDot';
import { useDarkMode } from '../context/DarkModeContext';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import useRoleSettings from '../../hooks/useRoleSettings';
import {
  GUEST_SYSTEM_KEY, groupRoleLabel, normalizeParticipantGroup, normalizeRole, ROLE,
} from '../../util/participantGroups';
import '../list/List.css';
import '../input/Checkbox.css';
import '../add-on/AddOnChips.css';
import './MarkdownRenderer.css';

const SCREENSHOT_BASE = '/docs-screenshots/';

function themedFile(file, isDark) {
  const dot = file.lastIndexOf('.');
  const base = dot !== -1 ? file.slice(0, dot) : file;
  const ext  = dot !== -1 ? file.slice(dot) : '';
  return `${base}-${isDark ? 'dark' : 'light'}${ext}`;
}

function Screenshot({ file, caption }) {
  const { isDarkMode } = useDarkMode();
  // fallback: if light variant is missing, retry with dark variant before showing placeholder
  const [useDark, setUseDark] = useState(false);
  const [failed, setFailed] = useState(false);

  const src = `${SCREENSHOT_BASE}${themedFile(file, isDarkMode || useDark)}`;

  if (failed) {
    return (
      <div className="md-screenshot-placeholder">
        <span className="material-symbols-rounded md-screenshot-icon">image</span>
        <div className="md-screenshot-text">
          <span className="md-screenshot-file">Screenshot fehlt: <code>{file}</code></span>
          <span className="md-screenshot-caption">{caption}</span>
        </div>
      </div>
    );
  }

  return (
    <figure className="md-screenshot">
      <img
        className="md-screenshot-img"
        src={src}
        alt={caption}
        onError={() => {
          if (!isDarkMode && !useDark) {
            setUseDark(true);
          } else {
            setFailed(true);
          }
        }}
      />
      <figcaption className="md-screenshot-caption-fig">{caption}</figcaption>
    </figure>
  );
}

// The system group guest — shown while the group list loads (or when it cannot be loaded).
const FALLBACK_GROUPS = {
  [GUEST_SYSTEM_KEY]: normalizeParticipantGroup({ id: -2, name: 'Gast', colorKey: 'neutral', systemKey: GUEST_SYSTEM_KEY, isGuest: true }),
};

const ROLE_ALIASES = { orga: ROLE.Organizer, organizer: ROLE.Organizer, admin: ROLE.Admin };

// {{participantgroup:<systemKey|id>[:<role>]}} → person dot + name with the configured group color,
// e.g. {{participantgroup:guest}}, {{participantgroup:3}}, {{participantgroup:guest:orga}} (role color
// overrides, label "Orga · Gast" — role name and color as configured in /groups › Rollen).
function ParticipantGroupToken({ groupRef, roleRef }) {
  const { groups } = useParticipantGroups();
  useRoleSettings();
  const ref = String(groupRef ?? '').trim();
  const group = (/^\d+$/.test(ref)
    ? groups.find(g => g.id === Number(ref))
    : groups.find(g => g.systemKey === ref))
    ?? FALLBACK_GROUPS[ref]
    ?? null;
  const role = normalizeRole(ROLE_ALIASES[String(roleRef ?? '').toLowerCase()] ?? roleRef ?? null);
  const label = groupRoleLabel(group, role) || ref;

  return (
    <span className="md-inline-participantgroup">
      <ParticipantGroupDot participantGroup={group} role={role} withTooltip={false} />
      <span className="md-inline-participantgroup-name">{label}</span>
    </span>
  );
}

// Inline shortcode: {{type:arg1[:arg2]}}
function renderShortcode(spec, key) {
  const parts = spec.split(':');
  const type = parts[0];
  if (type === 'eventstatus') {
    // Optional template rules as 3rd part (joined with "+"): {{eventstatus:5}} public stages,
    // {{eventstatus:1:neverpublic}} never-public events, {{eventstatus:5:neverpublic+linksignup}}
    // never-public events with invitation link.
    const rules = new Set(String(parts[2] ?? '').toLowerCase().split('+').map(r => r.trim()).filter(Boolean));
    return (
      <span key={key} className="md-inline-eventstatus">
        <EventStatus statusId={Number(parts[1])} neverPublic={rules.has('neverpublic')} linkSignup={rules.has('linksignup')} />
      </span>
    );
  }
  if (type === 'participantgroup') {
    return <ParticipantGroupToken key={key} groupRef={parts[1]} roleRef={parts[2]} />;
  }
  if (type === 'statusdot') {
    // {{statusdot:<event|yesno>:N}}
    return <span key={key} className="md-inline-statusdot"><StatusDot type={parts[1]} statusId={Number(parts[2])} /></span>;
  }
  if (type === 'addon') {
    // {{addon:<label>[:warning]}} → add-on chip as in the lists, e.g. {{addon:BBs ×2}}, {{addon:Leihausrüstung 5/8}},
    // {{addon:Leihausrüstung 8/8:warning}} (exhausted counter). Same classes as AddOnChips, but spans only, so the
    // chip can sit inside a paragraph.
    const warning = String(parts[2] ?? '').toLowerCase() === 'warning';
    return (
      <span key={key} className="addOnChips isCompact md-inline-addon">
        <span className={`addOnChip${warning ? ' isWarning' : ''}`}>{parts[1]}</span>
      </span>
    );
  }
  if (type === 'group') {
    const n = parts[1];
    const cls = n === 'X' ? 'groupX' : `group${n}`;
    return (
      <svg key={key} className={`listColorExtensionItem ${cls} md-inline-group`} width="14" height="14" viewBox="0 0 15 15" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 2V12C1 16 3 18 7 18H17L1 2Z" />
      </svg>
    );
  }
  if (type === 'icon') {
    const accent = parts[2];
    const cls = accent ? `legend-icon legend-icon--${accent}` : 'legend-icon';
    return <span key={key} className={`material-symbols-rounded md-inline-icon ${cls}`}>{parts[1]}</span>;
  }
  if (type === 'check') {
    const variant = parts[1] || 'present';

    const iconName =
      variant === 'notPresent'
        ? 'close'
        : variant === 'unset'
          ? 'radio_button_unchecked'
          : 'done';

    return (
      <span
        key={key}
        className={`material-symbols-rounded md-inline-check md-inline-check--${variant}`}
      >
        {iconName}
      </span>
    );
  }
  return <span key={key}>{`{{${spec}}}`}</span>;
}

function renderInline(text, keyPrefix) {
  const tokens = [];
  let remaining = text;
  let i = 0;

  while (remaining.length > 0) {
    const shortcodeMatch = remaining.match(/^\{\{([^}]+)\}\}/);
    if (shortcodeMatch) {
      tokens.push(renderShortcode(shortcodeMatch[1], `${keyPrefix}-${i++}`));
      remaining = remaining.slice(shortcodeMatch[0].length);
      continue;
    }
    const boldMatch = remaining.match(/^\*\*([^*]+)\*\*/);
    if (boldMatch) {
      tokens.push(<strong key={`${keyPrefix}-${i++}`}>{boldMatch[1]}</strong>);
      remaining = remaining.slice(boldMatch[0].length);
      continue;
    }
    const italicMatch = remaining.match(/^\*([^*]+)\*/);
    if (italicMatch) {
      tokens.push(<em key={`${keyPrefix}-${i++}`}>{italicMatch[1]}</em>);
      remaining = remaining.slice(italicMatch[0].length);
      continue;
    }
    const codeMatch = remaining.match(/^`([^`]+)`/);
    if (codeMatch) {
      tokens.push(<code key={`${keyPrefix}-${i++}`} className="md-inline-code">{codeMatch[1]}</code>);
      remaining = remaining.slice(codeMatch[0].length);
      continue;
    }
    const next = remaining.search(/(\{\{|\*\*|\*|`)/);
    if (next === -1) {
      tokens.push(remaining);
      break;
    }
    tokens.push(remaining.slice(0, next));
    remaining = remaining.slice(next);
  }

  return tokens;
}

function parseBlocks(md) {
  const lines = md.split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === '') {
      i++;
      continue;
    }

    // Screenshot placeholder block: [[screenshot: file.png | Caption]]
    const screenshotMatch = line.match(/^\[\[screenshot:\s*([^|\]]+?)\s*\|\s*(.+?)\s*\]\]\s*$/);
    if (screenshotMatch) {
      blocks.push({ type: 'screenshot', file: screenshotMatch[1], caption: screenshotMatch[2] });
      i++;
      continue;
    }

    if (line.startsWith('### ')) { blocks.push({ type: 'h3', text: line.slice(4) }); i++; continue; }
    if (line.startsWith('## '))  { blocks.push({ type: 'h2', text: line.slice(3) }); i++; continue; }
    if (line.startsWith('# '))   { blocks.push({ type: 'h1', text: line.slice(2) }); i++; continue; }

    if (line.startsWith('> ')) {
      const quoteLines = [];
      while (i < lines.length && lines[i].startsWith('> ')) {
        quoteLines.push(lines[i].slice(2));
        i++;
      }
      blocks.push({ type: 'quote', text: quoteLines.join(' ') });
      continue;
    }

    if (/^[-*] /.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*] /.test(lines[i])) {
        items.push(lines[i].slice(2));
        i++;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    if (/^\d+\. /.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\. /, ''));
        i++;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    const paraLines = [];
    while (i < lines.length && lines[i].trim() !== '' && !lines[i].startsWith('#') && !lines[i].startsWith('> ') && !/^[-*] /.test(lines[i]) && !/^\d+\. /.test(lines[i]) && !lines[i].match(/^\[\[screenshot:/)) {
      paraLines.push(lines[i]);
      i++;
    }
    blocks.push({ type: 'p', text: paraLines.join(' ') });
  }

  return blocks;
}

export default function MarkdownRenderer({ markdown }) {
  const blocks = parseBlocks(markdown);

  return (
    <div className="md-content">
      {blocks.map((b, i) => {
        const key = `b-${i}`;
        if (b.type === 'h1') return <h1 key={key} className="md-h1">{renderInline(b.text, key)}</h1>;
        if (b.type === 'h2') return <h2 key={key} className="md-h2">{renderInline(b.text, key)}</h2>;
        if (b.type === 'h3') return <h3 key={key} className="md-h3">{renderInline(b.text, key)}</h3>;
        if (b.type === 'p')  return <p  key={key} className="md-p">{renderInline(b.text, key)}</p>;
        if (b.type === 'quote') return <blockquote key={key} className="md-quote">{renderInline(b.text, key)}</blockquote>;
        if (b.type === 'ul') {
          return (
            <ul key={key} className="md-ul">
              {b.items.map((item, j) => (
                <li key={`${key}-${j}`}>{renderInline(item, `${key}-${j}`)}</li>
              ))}
            </ul>
          );
        }
        if (b.type === 'ol') {
          return (
            <ol key={key} className="md-ol">
              {b.items.map((item, j) => (
                <li key={`${key}-${j}`}>{renderInline(item, `${key}-${j}`)}</li>
              ))}
            </ol>
          );
        }
        if (b.type === 'screenshot') {
          return (
            <Screenshot
              key={key}
              file={b.file}
              caption={b.caption}
            />
          );
        }
        return null;
      })}
    </div>
  );
}
